import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from '@/lib/router';
import {
  FileText,
  LayoutGrid,
  LogOut,
  type LucideIcon,
  Menu,
  MessageSquare,
  Moon,
  Search,
  Settings,
  Sun,
  Table,
  Workflow,
  X,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAppAccess } from '../../contexts/AppAccessContext';
import { useTheme } from '../../contexts/ThemeContext';
import { CLAUDE_INTEGRATIONS_URL, CONNECTOR_URL } from '../../lib/connector';
import { cn } from '@/lib/cn';
import { headerToggle } from '@/lib/ui';
import { useDialogFocus } from '../../lib/useDialogFocus';

/**
 * Persistent global section navigation (CC-SPEC app-side MCP rewiring, Part 3).
 *
 * A header hamburger that opens a left slide-out drawer for jumping between
 * app sections. This is SEPARATE from the chat conversation sidebar
 * (Sidebar.tsx, conversations + matters) — AppNav is a higher-level section
 * switcher rendered on every authenticated page. Contents are gated by
 * `useAppAccess()`: the owner gets the full section list; a non-owner gets
 * only Account / Connect-in-Claude / Sign out (the app sections render
 * `ComingSoon` for them anyway).
 */

const OWNER_SECTIONS: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/chat', label: 'Chat', icon: MessageSquare },
  { to: '/research', label: 'Research', icon: Search },
  { to: '/claim-charts', label: 'Claim Charts', icon: LayoutGrid },
  { to: '/tabular-reviews', label: 'Tabular Reviews', icon: Table },
  { to: '/workflows', label: 'Workflows', icon: Workflow },
  { to: '/document-editor', label: 'Document Editor', icon: FileText },
  { to: '/account', label: 'Account', icon: Settings },
];

const drawerLink =
  'flex items-center gap-3 px-3 py-2 rounded-md font-ui text-sm text-text-secondary no-underline cursor-pointer transition-colors hover:bg-surface-1 hover:text-text-primary aria-[current=page]:bg-navy-100 aria-[current=page]:text-navy-700 aria-[current=page]:font-medium';

const drawerRow =
  'flex items-center gap-3 px-3 py-2 rounded-md font-ui text-sm text-text-secondary bg-transparent w-full text-left cursor-pointer transition-colors hover:bg-surface-1 hover:text-text-primary';

export function AppNav() {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const { appAccess } = useAppAccess();
  const { theme, toggleTheme } = useTheme();
  const [open, setOpen] = useState(false);
  const dialogRef = useDialogFocus<HTMLDivElement>(open);

  // Close on Escape while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const isOwner = appAccess === true;
  const close = () => setOpen(false);

  return (
    <>
      <button
        type="button"
        className={headerToggle}
        aria-label="Open navigation menu"
        aria-haspopup="dialog"
        aria-controls={open ? 'global-navigation-dialog' : undefined}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <Menu size={20} />
      </button>

      {open && (
        <div
          ref={dialogRef}
          id="global-navigation-dialog"
          className="fixed inset-0 z-[1100]"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation"
          tabIndex={-1}
        >
          <div
            className="absolute inset-0 bg-[rgba(26,22,18,0.36)]"
            onClick={close}
            aria-hidden="true"
          />
          <nav className="absolute inset-y-0 left-0 w-[280px] max-w-[85vw] flex flex-col bg-surface-white border-r border-border shadow-[0_20px_60px_rgba(26,22,18,0.18)]">
            <div className="flex-shrink-0 h-12 flex items-center justify-between px-4 border-b border-border">
              <span className="font-chrome text-sm font-semibold tracking-[0.05em] uppercase text-text-primary">
                Prometheus
              </span>
              <button
                type="button"
                className="inline-flex items-center justify-center w-7 h-7 rounded-sm text-text-tertiary bg-transparent hover:bg-surface-1 hover:text-text-primary"
                onClick={close}
                aria-label="Close navigation menu"
              >
                <X size={16} />
              </button>
            </div>

            <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-1 p-2">
              {isOwner ? (
                OWNER_SECTIONS.map((s) => {
                  const Icon = s.icon;
                  return (
                    <NavLink key={s.to} to={s.to} className={drawerLink} onClick={close}>
                      <Icon size={16} aria-hidden="true" />
                      <span>{s.label}</span>
                    </NavLink>
                  );
                })
              ) : (
                <>
                  <NavLink to="/account" className={drawerLink} onClick={close}>
                    <Settings size={16} aria-hidden="true" />
                    <span>Account</span>
                  </NavLink>
                  <a
                    href={CLAUDE_INTEGRATIONS_URL}
                    target="_blank"
                    rel="noreferrer noopener"
                    className={drawerLink}
                    onClick={close}
                  >
                    <LayoutGrid size={16} aria-hidden="true" />
                    <span>Connect in Claude</span>
                  </a>
                  <p className="px-3 pt-1 pb-2 font-ui text-[11px] text-text-tertiary break-all m-0">
                    {CONNECTOR_URL}
                  </p>
                </>
              )}
            </div>

            <div className="flex-shrink-0 border-t border-border p-2 flex flex-col gap-1">
              <button
                type="button"
                className={drawerRow}
                onClick={() => {
                  toggleTheme();
                }}
              >
                {theme === 'dark' ? <Sun size={16} aria-hidden="true" /> : <Moon size={16} aria-hidden="true" />}
                <span>Theme: {theme === 'dark' ? 'Dark' : 'Light'}</span>
              </button>
              {user?.email && (
                <p className="px-3 py-1 font-ui text-xs text-text-tertiary break-all m-0">
                  {user.email}
                </p>
              )}
              <button
                type="button"
                className={cn(drawerRow, 'text-error-text hover:bg-error-bg hover:text-error-text')}
                onClick={() => {
                  close();
                  void signOut();
                  navigate('/');
                }}
              >
                <LogOut size={16} aria-hidden="true" />
                <span>Sign out</span>
              </button>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
